import {
  generateToggleFieldsFromFormFields,
  getFirstUnresolvedToggleFormFieldKey,
  isToggleFormFieldVisible,
  useGeckoForm,
} from 'components/form/formHelpers'
import {
  MeasurementApplication,
  PermissionsCheck,
} from 'components/form/formTypes'
import React, { forwardRef, ReactNode, useMemo } from 'react'
import { FormProvider } from 'react-hook-form'

import { ToggleFormField } from './formFields/ToggleFormField'
import { FormComponentProps, StyledForm } from './GeckoForm'
import { CoordinateInputTablePointAndClickApi } from './inputs/coordinatesInput/CoordinatesInputTable'
import { Coordinate } from './inputs/coordinatesInput/helpers/coordinateTranslations'

interface GeckoToggleFormProps extends FormComponentProps {
  inEditMode: boolean
  inResolveMode?: boolean
  measurementApplication?: MeasurementApplication
  permissionsCheck?: PermissionsCheck
  resetOnSubmit?: boolean
  pointAndClickApi: CoordinateInputTablePointAndClickApi | undefined
  highlightCoordinate: (coordinate: Coordinate | null) => void
  displayCoordinateUpdates: (newCoords: ReadonlyArray<Coordinate>) => void
  /**
   * Optional per-field accessory rendered inline beside each field input, keyed
   * by the field's `dataKey`. Return `null`/`undefined` for fields that should
   * have no accessory. Defaults to no accessories (bare fields, unchanged
   * layout).
   */
  renderFieldAccessory?: (dataKey: string) => ReactNode
}

export const GeckoToggleForm = forwardRef<
  HTMLFormElement,
  GeckoToggleFormProps
>(
  (
    {
      inEditMode,
      inResolveMode,
      measurementApplication,
      permissionsCheck,
      formFieldsByUniqueID,
      onSubmit,
      numberOfColumns = 2,
      propForm,
      resetOnSubmit,
      pointAndClickApi,
      highlightCoordinate,
      displayCoordinateUpdates,
      renderFieldAccessory,
    },
    ref
  ) => {
    const useForm = useGeckoForm(formFieldsByUniqueID)
    const form = propForm ?? useForm
    const firstUnresolvedFormItemKey = useMemo(
      () =>
        getFirstUnresolvedToggleFormFieldKey(
          formFieldsByUniqueID,
          inResolveMode ?? false,
          form.watch,
          measurementApplication,
          permissionsCheck
        ),
      [
        formFieldsByUniqueID,
        inResolveMode,
        form.watch,
        measurementApplication,
        permissionsCheck,
      ]
    )

    return (
      <FormProvider {...form}>
        <StyledForm
          $columns={numberOfColumns}
          onSubmit={form.handleSubmit((data) => {
            onSubmit(data)
            if (resetOnSubmit) {
              form.reset()
            }
          })}
          ref={ref}>
          {formFieldsByUniqueID.map((formFieldsByUniqueID) =>
            generateToggleFieldsFromFormFields(
              formFieldsByUniqueID.formFields,
              formFieldsByUniqueID.uniqueID,
              measurementApplication,
              permissionsCheck
            ).map((formField) => {
              const shouldShow = isToggleFormFieldVisible(
                formField,
                formFieldsByUniqueID.formFields,
                form.watch
              )
              if (!shouldShow) return null
              const field = (
                <ToggleFormField
                  key={formField.formItemKey}
                  formItemKey={formField.formItemKey}
                  formField={formField.formField}
                  fieldValue={formField.formFieldValue}
                  fieldUnits={formField.formFieldUnits}
                  canEditField={formField.canEditField}
                  canReadField={formField.canReadField}
                  units={formField.units}
                  formControl={form.control}
                  formErrors={form.formState.errors}
                  formTrigger={form.trigger}
                  formSetValue={form.setValue}
                  inEditMode={inEditMode}
                  inResolveMode={inResolveMode}
                  focusOnResolve={
                    formField.formItemKey === firstUnresolvedFormItemKey
                  }
                  pointAndClickApi={pointAndClickApi}
                  highlightCoordinate={highlightCoordinate}
                  displayCoordinateUpdates={displayCoordinateUpdates}
                />
              )
              const accessory = renderFieldAccessory?.(
                formField.formField.dataKey
              )
              if (accessory === null || accessory === undefined) return field
              // Render the accessory as a sibling grid item after the field; it
              // owns its own layout (indent, etc.). In the single-column FFS grid
              // it naturally falls on the next row.
              return (
                <React.Fragment key={formField.formItemKey}>
                  {field}
                  {accessory}
                </React.Fragment>
              )
            })
          )}
        </StyledForm>
      </FormProvider>
    )
  }
)

GeckoToggleForm.displayName = 'GeckoToggleForm'
